/**
 * Applying server copies to the local library: plain updates, divergences
 * (both sides changed), conflict resolution and version history restores.
 */

import {
  getNonDeletedElements,
  hashElementsVersion,
} from "@excalidraw/element";

import { LibraryAPI } from "./api";
import {
  createDrawing,
  getReferencedFileIds,
  loadDrawing,
  makeSceneDominant,
  mergeDrawingScenes,
  restoreDrawingElements,
  sameElements,
} from "./drawings";
import { formatShortDateTime, lt } from "./i18n";
import { getConflictMode, getDeviceName } from "./settings";
import { DrawingStore, emitLibraryChange } from "./store";

import type {
  ConflictResolution,
  DrawingId,
  DrawingMeta,
  DrawingScene,
  ServerDrawing,
} from "./types";

export const pullFiles = async (fileIds: readonly string[]) => {
  for (const fileId of await DrawingStore.getMissingFileIds(fileIds)) {
    const file = await LibraryAPI.getFile(fileId);
    if (file) {
      await DrawingStore.putFiles([file]);
    }
  }
};

const sceneStats = (scene: DrawingScene) => ({
  elementsHash: hashElementsVersion(scene.elements),
  elementCount: getNonDeletedElements(scene.elements).length,
  fileIds: getReferencedFileIds(scene.elements),
});

/** replaces the local copy with the server one */
export const storeServerCopy = async (
  drawing: ServerDrawing,
  overrides: Partial<DrawingMeta> = {},
) => {
  const scene = drawing.scene!;
  await pullFiles(drawing.fileIds);
  const name = overrides.name ?? drawing.name;
  await DrawingStore.putScene(drawing.id, {
    elements: scene.elements,
    appState: { ...scene.appState, name },
  });
  await DrawingStore.deleteConflictCopy(drawing.id);
  await DrawingStore.updateMeta(drawing.id, () => ({
    id: drawing.id,
    name: drawing.name,
    createdAt: drawing.createdAt,
    updatedAt: drawing.updatedAt,
    metaUpdatedAt: drawing.metaUpdatedAt ?? drawing.updatedAt,
    deleted: drawing.deleted,
    purged: false,
    folderId: drawing.folderId,
    thumbnail: drawing.thumbnail,
    ...sceneStats(scene),
    fileIds: drawing.fileIds,
    serverVersion: drawing.version,
    dirty: false,
    localSavedAt: Date.now(),
    conflict: null,
    snapshot: false,
    updatedBy: drawing.updatedBy ?? null,
    ...overrides,
  }));
};

/** writes new local content for an existing drawing */
const storeLocalScene = async (
  id: DrawingId,
  scene: DrawingScene,
  overrides: Partial<DrawingMeta>,
) => {
  const now = Date.now();
  const meta = await DrawingStore.updateMeta(id, (current) =>
    current
      ? {
          ...current,
          ...sceneStats(scene),
          updatedAt: now,
          localSavedAt: now,
          dirty: true,
          conflict: null,
          ...overrides,
        }
      : undefined,
  );
  if (meta) {
    await DrawingStore.putScene(id, {
      elements: scene.elements,
      appState: { ...scene.appState, name: meta.name },
    });
  }
};

export type DivergenceOutcome = "local" | "remote" | "merged" | "conflict";

/**
 * The local copy has unpushed changes and the server has a newer version.
 * Changes on one side only are applied silently; real conflicts (both sides
 * changed the elements) are merged or kept for the user to resolve,
 * depending on the preference.
 */
export const handleDivergence = async (
  remote: ServerDrawing,
): Promise<DivergenceOutcome> => {
  const id = remote.id;
  const [meta, scene] = await Promise.all([
    DrawingStore.getMeta(id),
    DrawingStore.getScene(id),
  ]);
  if (!meta || !scene || meta.purged || !remote.scene) {
    if (remote.scene) {
      await storeServerCopy(remote);
    }
    return "remote";
  }

  const merged = mergeDrawingScenes(scene, remote.scene);
  const remoteHasNew = !sameElements(merged.elements, scene.elements);
  const localHasNew = !sameElements(merged.elements, remote.scene.elements);

  // metadata (name, folder, trash): the most recent change wins, but a
  // drawing edited on one side stays alive even if trashed on the other
  const localIsNewer =
    (meta.metaUpdatedAt ?? meta.updatedAt) >=
    (remote.metaUpdatedAt ?? remote.updatedAt);
  const name = localIsNewer ? meta.name : remote.name;
  const folderId = localIsNewer ? meta.folderId : remote.folderId;
  const deleted =
    (localHasNew && !meta.deleted) || (remoteHasNew && !remote.deleted)
      ? false
      : localIsNewer
      ? meta.deleted
      : remote.deleted;
  const metadata = {
    name,
    folderId,
    deleted,
    updatedAt: Math.max(meta.updatedAt, remote.updatedAt),
    metaUpdatedAt: Math.max(
      meta.metaUpdatedAt ?? meta.updatedAt,
      remote.metaUpdatedAt ?? remote.updatedAt,
    ),
  };
  const metadataDiffers =
    name !== remote.name ||
    folderId !== remote.folderId ||
    deleted !== remote.deleted;

  if (!remoteHasNew) {
    // only this device changed the content: keep it, based on the new version
    await DrawingStore.deleteConflictCopy(id);
    await DrawingStore.updateMeta(id, (current) =>
      current
        ? {
            ...current,
            ...metadata,
            serverVersion: remote.version,
            dirty: localHasNew || metadataDiffers,
            conflict: null,
          }
        : undefined,
    );
    return "local";
  }

  if (!localHasNew) {
    // only the other device changed the content (here maybe just thumbnail
    // or metadata changes)
    await storeServerCopy(remote, {
      ...metadata,
      dirty: metadataDiffers,
    });
    return "remote";
  }

  await pullFiles(remote.fileIds);

  if (getConflictMode() === "merge") {
    await DrawingStore.deleteConflictCopy(id);
    await storeLocalScene(id, merged, {
      ...metadata,
      serverVersion: remote.version,
      thumbnail: null,
      updatedBy: remote.updatedBy ?? null,
    });
    return "merged";
  }

  await DrawingStore.putConflictCopy(remote);
  await DrawingStore.updateMeta(id, (current) =>
    current
      ? {
          ...current,
          conflict: {
            serverVersion: remote.version,
            remoteUpdatedAt: remote.savedAt ?? remote.updatedAt,
            remoteDevice: remote.updatedBy ?? null,
            detectedAt: Date.now(),
          },
        }
      : undefined,
  );
  return "conflict";
};

/** loads both sides of a conflict (for the resolution dialog) */
export const loadConflict = async (id: DrawingId) => {
  const [local, remote] = await Promise.all([
    loadDrawing(id),
    DrawingStore.getConflictCopy(id),
  ]);
  if (!local?.meta.conflict || !remote?.scene) {
    return null;
  }
  const remoteFiles = await DrawingStore.getFiles(remote.fileIds);
  return {
    local,
    remote: {
      drawing: remote,
      scene: remote.scene,
      files: Object.fromEntries(remoteFiles.map((file) => [file.id, file])),
    },
  };
};

/** applies the user's choice for a drawing in conflict */
export const resolveConflict = async (
  id: DrawingId,
  resolution: ConflictResolution,
) => {
  const [local, remote] = await Promise.all([
    loadDrawing(id),
    DrawingStore.getConflictCopy(id),
  ]);
  if (!local || !remote?.scene) {
    await DrawingStore.updateMeta(id, (current) =>
      current ? { ...current, conflict: null } : undefined,
    );
    return;
  }

  switch (resolution) {
    case "theirs":
      await storeServerCopy(remote);
      break;
    case "mine":
      await storeLocalScene(id, makeSceneDominant(local.scene, remote.scene), {
        serverVersion: remote.version,
        snapshot: true,
      });
      break;
    case "merge":
      await storeLocalScene(id, mergeDrawingScenes(local.scene, remote.scene), {
        serverVersion: remote.version,
        snapshot: true,
        thumbnail: null,
      });
      break;
    case "both":
      await createDrawing({
        name: `${local.meta.name} (${lt("conflictCopyName", {
          device: getDeviceName(),
          date: formatShortDateTime(Date.now()),
        })})`,
        elements: restoreDrawingElements(
          getNonDeletedElements(local.scene.elements),
        ),
        appState: local.scene.appState,
        files: local.files,
        folderId: local.meta.folderId,
      });
      await storeServerCopy(remote);
      break;
  }
  await DrawingStore.deleteConflictCopy(id);
  await DrawingStore.collectGarbageFiles();
  emitLibraryChange([id], "replace");
};

/** fetches a version from the server history, with its files */
export const loadServerVersion = async (id: DrawingId, version: number) => {
  const drawing = await LibraryAPI.getVersion(id, version);
  await pullFiles(drawing.fileIds);
  const files = await DrawingStore.getFiles(drawing.fileIds);
  return {
    drawing,
    scene: drawing.scene!,
    files: Object.fromEntries(files.map((file) => [file.id, file])),
  };
};

/** makes a version from the server history the current content (a new
 * version: the history is never rewritten) */
export const restoreServerVersion = async (id: DrawingId, version: number) => {
  const { drawing, scene } = await loadServerVersion(id, version);
  const meta = await DrawingStore.getMeta(id);
  const current = await DrawingStore.getScene(id);
  const restored = makeSceneDominant(scene, current);
  const remoteCopy = meta?.conflict
    ? await DrawingStore.getConflictCopy(id)
    : null;
  await DrawingStore.deleteConflictCopy(id);
  await storeLocalScene(id, makeSceneDominant(restored, remoteCopy?.scene), {
    name: drawing.name,
    thumbnail: drawing.thumbnail,
    deleted: false,
    snapshot: true,
    ...(meta?.conflict ? { serverVersion: meta.conflict.serverVersion } : {}),
  });
  emitLibraryChange([id], "replace");
};
